'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function ConsultationForm() {
  const [formData, setFormData] = useState({
    contactName: '',
    companyName: '',
    email: '',
    phone: '',
    consultationType: '',
    projectScope: '',
    technicalRequirements: '',
    timelineConstraints: '',
    preferredDate: '',
    preferredTime: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const consultationTypes = [
    'Process Selection',
    'Material Consultation',
    'Design for Manufacturing',
    'Quality Requirements',
    'Capacity Planning',
    'Other'
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Consultation request submitted:', formData);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-12 text-center">
        <div className="w-20 h-20 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircleIcon" size={48} variant="solid" className="text-success" />
        </div>
        <h2 className="text-3xl font-headline font-bold text-foreground mb-4">
          Consultation Scheduled!
        </h2>
        <p className="text-lg text-muted-foreground mb-8">
          Thank you for scheduling a consultation. Our expert team will contact you within 4 hours to confirm your appointment.
        </p>
        <button
          onClick={() => setIsSubmitted(false)}
          className="px-8 py-3 bg-secondary text-secondary-foreground rounded-lg font-cta font-bold hover:opacity-90 transition-all duration-base"
        >
          Schedule Another Consultation
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-12">
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center">
            <Icon name="ChatBubbleLeftRightIcon" size={24} variant="solid" className="text-secondary" />
          </div>
          <h2 className="text-3xl font-headline font-bold text-foreground">
            Schedule Consultation
          </h2>
        </div>
        <p className="text-muted-foreground font-body">
          Connect with our technical experts to discuss your manufacturing requirements and get personalized recommendations.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-cta font-semibold text-foreground">
              Contact Name <span className="text-error">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.contactName}
              onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
              placeholder="Your full name"
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-base"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-cta font-semibold text-foreground">
              Company Name
            </label>
            <input
              type="text"
              value={formData.companyName}
              onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
              placeholder="Your company name"
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-base"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-cta font-semibold text-foreground">
              Email Address <span className="text-error">*</span>
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your.email@company.com"
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-base"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-cta font-semibold text-foreground">
              Phone Number <span className="text-error">*</span>
            </label>
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="(555) 123-4567"
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-base"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-cta font-semibold text-foreground">
            Consultation Type <span className="text-error">*</span>
          </label>
          <select
            required
            value={formData.consultationType}
            onChange={(e) => setFormData({ ...formData, consultationType: e.target.value })}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-base"
          >
            <option value="">Select consultation type</option>
            {consultationTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-cta font-semibold text-foreground">
            Project Scope <span className="text-error">*</span>
          </label>
          <textarea
            required
            value={formData.projectScope}
            onChange={(e) => setFormData({ ...formData, projectScope: e.target.value })}
            placeholder="Describe your project scope and objectives..."
            rows={4}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-base resize-none"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-cta font-semibold text-foreground">
            Technical Requirements
          </label>
          <textarea
            value={formData.technicalRequirements}
            onChange={(e) => setFormData({ ...formData, technicalRequirements: e.target.value })}
            placeholder="Any specific technical requirements, tolerances, or certifications needed..."
            rows={3}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-base resize-none"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-cta font-semibold text-foreground">
            Timeline Constraints
          </label>
          <input
            type="text"
            value={formData.timelineConstraints}
            onChange={(e) => setFormData({ ...formData, timelineConstraints: e.target.value })}
            placeholder="e.g., Need to start production in 4 weeks"
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-base"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-cta font-semibold text-foreground">
              Preferred Date
            </label>
            <input
              type="date"
              value={formData.preferredDate}
              onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-base"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-cta font-semibold text-foreground">
              Preferred Time
            </label>
            <select
              value={formData.preferredTime}
              onChange={(e) => setFormData({ ...formData, preferredTime: e.target.value })}
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-base"
            >
              <option value="">Select time</option>
              <option value="morning">Morning (9 AM - 11 AM)</option>
              <option value="afternoon">Afternoon (1 PM - 4 PM)</option>
            </select>
          </div>
        </div>

        <div className="bg-secondary/10 rounded-xl p-6 border border-secondary/20">
          <div className="flex items-start space-x-3">
            <Icon name="UserGroupIcon" size={24} variant="solid" className="text-secondary flex-shrink-0" />
            <div className="space-y-2">
              <div className="text-sm font-cta font-semibold text-foreground">
                Expert Routing
              </div>
              <div className="text-sm text-muted-foreground font-body">
                Based on your consultation type, you'll be connected with the most qualified expert from our technical team.
              </div>
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="w-full px-8 py-4 bg-secondary text-secondary-foreground rounded-lg font-cta font-bold hover:opacity-90 transition-all duration-base shadow-lg flex items-center justify-center space-x-2"
        >
          <Icon name="CalendarIcon" size={20} variant="solid" />
          <span>Schedule Consultation</span>
        </button>
      </form>
    </div>
  );
}